import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Modal, Alert } from "react-bootstrap";
import { sweetAlert } from "../../../../../utils/UniversalFunction";
import { getStartingMonth } from "../../../../../utils/PeriodCalculationUtils";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";
import { COMPANY_TEMPLATE_MAPPING } from "./config/CompanyTemplateMapping";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import { useGenerateExcelTemplate } from "./template/useGenerateExcelTemplate";

/* ─── Helpers ─────────────────────────────────────────────────── */

const downloadTemplateFile = (fileName) => {
  fetch(`/${fileName}`)
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((blob) => {
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(blob),
        download: fileName,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    })
    .catch(() => sweetAlert("error", "Template download failed."));
};

const resolvePermissionPayload = (responseData = {}) => {
  if (Array.isArray(responseData?.data)) return responseData.data[0] || {};
  if (responseData?.data && !Array.isArray(responseData.data)) {
    const d = responseData.data;
    if (
      d.is_enabled !== undefined || d.isEnabled !== undefined ||
      d.supported_templates !== undefined || d.template_type !== undefined ||
      d.templateType !== undefined
    ) return d;
    if (d.data) return d.data;
  }
  return responseData;
};

const resolveIsEnabled = (permissionData) =>
  permissionData?.isEnabled ??
  permissionData?.is_enabled ??
  (Array.isArray(permissionData?.supported_templates)
    ? permissionData.supported_templates.length > 0
    : undefined) ??
  (permissionData?.templateType === "custom_template" ||
    permissionData?.template_type === "custom_template");

const STATUS_META = {
  SUCCESS: { color: "#22c55e", bg: "#f0fdf4", label: "Success" },
  PARTIAL_SUCCESS: { color: "#f59e0b", bg: "#fffbeb", label: "Partial" },
  SUBMITTED: { color: "#3b82f6", bg: "#eff6ff", label: "Submitted" },
  FAILURE: { color: "#ef4444", bg: "#fef2f2", label: "Failed" },
};

/* ─── SearchableSingleSelect ───────────────────────────────────── */

const SearchableSingleSelect = ({ options = [], value, onChange, placeholder, required = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const select = (opt) => {
    onChange(opt);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const isFocused = open;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          border: `1.5px solid ${isFocused ? "#3f88a5" : required && !value ? "#f59e0b" : "#e2eaf0"}`,
          borderRadius: 8,
          height: 38,
          padding: "0 36px 0 12px",
          background: "white",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          boxShadow: isFocused ? "0 0 0 3px rgba(63,136,165,0.1)" : "none",
          transition: "border-color 0.2s, box-shadow 0.2s",
          position: "relative",
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        <span style={{
          fontSize: "0.865rem",
          color: value ? "#1a2e38" : "#9ab4bf",
          fontWeight: value ? 500 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
        }}>
          {value ? value.label : placeholder}
        </span>

        {/* Right icons */}
        <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 4 }}>
          {value && (
            <span
              onClick={clear}
              style={{ color: "#b0c4ce", fontSize: "0.9rem", cursor: "pointer", lineHeight: 1, padding: "0 2px", fontWeight: 700 }}
              title="Clear"
            >×</span>
          )}
          <svg style={{ width: 10, height: 10, color: "#b0c4ce" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <polyline points={open ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          width: "100%",
          zIndex: 700,
          background: "white",
          border: "1.5px solid #e2eaf0",
          borderRadius: 10,
          boxShadow: "0 8px 28px rgba(0,0,0,0.13)",
          overflow: "hidden",
        }}>
          {/* Search */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f0f4f7" }}>
            <div style={{ position: "relative" }}>
              <svg style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", width: 11, height: 11, color: "#b0c4ce" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%", border: "1.5px solid #e2eaf0", borderRadius: 6,
                  padding: "5px 10px 5px 28px", fontSize: "0.83rem", color: "#1a2e38",
                  outline: "none", background: "#f7fafc", boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Options */}
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 14, fontSize: "0.82rem", color: "#9ab4bf", textAlign: "center" }}>No results</div>
            ) : (
              filtered.map(opt => {
                const isSelected = value?.value === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => select(opt)}
                    style={{
                      padding: "9px 14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      cursor: "pointer",
                      background: isSelected ? "rgba(63,136,165,0.07)" : "transparent",
                      transition: "background 0.1s",
                      fontSize: "0.865rem",
                      color: isSelected ? "#3f88a5" : "#1a2e38",
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f7fafc"; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                  >
                    {/* Dot indicator for selected */}
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      background: isSelected ? "#3f88a5" : "transparent",
                      border: `2px solid ${isSelected ? "#3f88a5" : "#d1dce2"}`,
                      transition: "all 0.15s",
                    }} />
                    {opt.label}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── MonthMultiSelect ─────────────────────────────────────────── */

const MonthMultiSelect = ({ options = [], selected = [], onChange, disabled = false, placeholder = "Select month(s)", selectAllLabel = "All months" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedSet = new Set(selected.map(s => s.name));

  const toggle = (opt) => {
    if (disabled) return;
    if (selectedSet.has(opt.name)) onChange(selected.filter(s => s.name !== opt.name));
    else onChange([...selected, opt]);
  };

  const toggleAll = () => {
    if (disabled) return;
    if (selected.length === options.length) onChange([]);
    else onChange([...options]);
  };

  const removeChip = (e, name) => {
    e.stopPropagation();
    onChange(selected.filter(s => s.name !== name));
  };

  const allSelected = selected.length === options.length && options.length > 0;

  return (
    <div ref={ref} style={{ position: "relative", opacity: disabled ? 0.55 : 1 }}>
      {/* Trigger */}
      <div
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        style={{
          border: `1.5px solid ${open ? "#3f88a5" : "#e2eaf0"}`,
          borderRadius: 8,
          minHeight: 38,
          padding: selected.length > 0 ? "5px 36px 5px 8px" : "0 36px 0 12px",
          background: disabled ? "#f8fafc" : "white",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 4,
          boxShadow: open ? "0 0 0 3px rgba(63,136,165,0.1)" : "none",
          transition: "border-color 0.2s",
          position: "relative",
          userSelect: "none",
        }}
      >
        {selected.length === 0 ? (
          <span style={{ fontSize: "0.865rem", color: "#9ab4bf" }}>{placeholder}</span>
        ) : selected.length <= 4 ? (
          selected.map(s => (
            <span
              key={s.name}
              style={{
                background: "rgba(63,136,165,0.1)", color: "#3f88a5",
                borderRadius: 5, padding: "2px 7px 2px 8px",
                fontSize: "0.78rem", fontWeight: 600,
                display: "flex", alignItems: "center", gap: 4,
              }}
            >
              {s.displayName}
              <span onClick={(e) => removeChip(e, s.name)} style={{ cursor: "pointer", opacity: 0.6, fontWeight: 700, fontSize: "0.8rem" }}>×</span>
            </span>
          ))
        ) : (
          <span style={{ background: "rgba(63,136,165,0.1)", color: "#3f88a5", borderRadius: 5, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 700 }}>
            {selected.length} months selected
          </span>
        )}

        <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 4 }}>
          {selected.length > 0 && !disabled && (
            <span onClick={(e) => { e.stopPropagation(); onChange([]); }} style={{ color: "#b0c4ce", fontSize: "0.9rem", cursor: "pointer", fontWeight: 700 }}>×</span>
          )}
          <svg style={{ width: 10, height: 10, color: "#b0c4ce" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <polyline points={open ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, width: "100%",
          zIndex: 700, background: "white",
          border: "1.5px solid #e2eaf0", borderRadius: 10,
          boxShadow: "0 8px 28px rgba(0,0,0,0.13)", overflow: "hidden",
        }}>
          {/* Select all */}
          {options.length > 1 && (
            <div
              onClick={toggleAll}
              style={{
                padding: "9px 14px", display: "flex", alignItems: "center", gap: 9,
                cursor: "pointer", borderBottom: "1px solid #f0f4f7",
                background: allSelected ? "rgba(63,136,165,0.05)" : "white",
              }}
            >
              <MiniCheckbox checked={allSelected} />
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#3f88a5" }}>{selectAllLabel}</span>
            </div>
          )}

          {/* Month grid — 3 columns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "6px 8px 8px" }}>
            {options.map(opt => {
              const isChecked = selectedSet.has(opt.name);
              return (
                <div
                  key={opt.name}
                  onClick={() => toggle(opt)}
                  style={{
                    padding: "8px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    borderRadius: 6,
                    background: isChecked ? "rgba(63,136,165,0.07)" : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = "#f7fafc"; }}
                  onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.background = isChecked ? "rgba(63,136,165,0.07)" : "transparent"; }}
                >
                  <MiniCheckbox checked={isChecked} />
                  <span style={{ fontSize: "0.84rem", color: isChecked ? "#3f88a5" : "#1a2e38", fontWeight: isChecked ? 600 : 400 }}>
                    {opt.displayName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const MiniCheckbox = ({ checked }) => (
  <div style={{
    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
    border: `2px solid ${checked ? "#3f88a5" : "#c8d8df"}`,
    background: checked ? "#3f88a5" : "white",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  }}>
    {checked && (
      <svg style={{ width: 8, height: 8 }} viewBox="0 0 12 12" fill="none">
        <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
);

/* ─── Sub-components ───────────────────────────────────────────── */

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { color: "#6b7280", bg: "#f3f4f6", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      color: meta.color, background: meta.bg, border: `1px solid ${meta.color}22`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.color }} />
      {meta.label}
    </span>
  );
};

const HistoryRow = ({ record, idx }) => {
  const date = new Date(record.createdAt);
  const periods = Array.isArray(record.periods) ? record.periods : [];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "28px 1fr 1fr 90px 80px",
      alignItems: "center",
      gap: 12,
      padding: "10px 16px",
      borderBottom: "1px solid #f1f5f9",
      background: idx % 2 === 0 ? "#fff" : "#fafbfc",
      fontSize: 13,
    }}>
      <span style={{ color: "#94a3b8", fontWeight: 600 }}>{idx + 1}</span>
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 1 }}>
          {date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <div style={{ color: "#64748b", fontSize: 11 }}>
          {date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <div style={{ color: "#475569" }}>
        {periods.length > 0
          ? `${periods[0].fromDate} → ${periods[periods.length - 1].toDate}`
          : "—"}
        {periods.length > 1 && (
          <span style={{ marginLeft: 4, color: "#94a3b8", fontSize: 11 }}>
            ({periods.length} periods)
          </span>
        )}
      </div>
      <div style={{ color: "#475569" }}>{record.uploadedRowCount ?? "—"} rows</div>
      <StatusPill status={record.status} />
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────────── */

const ExcelUploader = ({
  financialYearId,
  financialYear,
}) => {
  const { companyId } = useCurrentUser();

  /* upload state */
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);   // { id, name, subLocations }
  const [selectedSubLocation, setSelectedSubLocation] = useState(null); // { id, name } | null
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scheduledDateData, setScheduledDateData] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [uploadPermission, setUploadPermission] = useState({ templateDefined: false, isEnabled: false });
  const [dragOver, setDragOver] = useState(false);
  const [periodGranularity, setPeriodGranularity] = useState("monthly"); // 'monthly' | 'quarterly'
  const [templateSource, setTemplateSource] = useState(null); // 'custom' | 'standard'  (resolved by backend)
  const { generate: generateStandardTemplate, loading: templateLoading } = useGenerateExcelTemplate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/excel-template/source`,
        {}, {}, "GET", false, false
      );
      if (cancelled) return;
      setTemplateSource(isSuccess && data?.source === "custom" ? "custom" : "standard");
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const quarters = useMemo(() => {
    const monthWithYear = (m) => {
      const [, yyyy] = (m.name || "").split("/");
      return yyyy ? `${m.displayName} ${yyyy}` : m.displayName;
    };
    const out = [];
    for (let i = 0; i < months.length; i += 3) {
      const slice = months.slice(i, i + 3);
      if (slice.length === 0) continue;
      const qIdx = Math.floor(i / 3) + 1;
      const start = monthWithYear(slice[0]);
      const end = monthWithYear(slice[slice.length - 1]);
      const fullLabel = `Q${qIdx} (${start} - ${end})`;
      out.push({
        name: `Q${qIdx}`,
        displayName: fullLabel,
        label: fullLabel,
        months: slice,
      });
    }
    return out;
  }, [months]);

  const periodOptions = periodGranularity === "monthly" ? months : quarters;

  useEffect(() => {
    setSelectedMonths(periodOptions ?? []);
  }, [periodOptions]);

  /* history state */
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* ── API calls ── */

  const fetchPermission = async () => {
    setPermissionLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}company-template-permission`,
        {}, { companyId }, "GET"
      );
      if (!isSuccess) { setUploadPermission({ templateDefined: true, isEnabled: false }); return; }
      const pd = resolvePermissionPayload(data);
      setUploadPermission({ templateDefined: true, isEnabled: Boolean(Number(resolveIsEnabled(pd))) });
    } catch {
      setUploadPermission({ templateDefined: true, isEnabled: false });
    } finally {
      setPermissionLoading(false);
    }
  };

  const getLocations = async () => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET"
      );
      if (isSuccess && data?.data) {
        // Preserve full structure: { id, name, subLocations: [{id, name}] }
        const opts = data.data.map((item) => ({
          id: item.id,
          name: item.unitCode || item.location?.city || String(item.id),
          subLocations: (item.subLocation || []).map((sub) => ({
            id: sub.id,
            name: sub.subLocation,
          })),
        }));
        setLocations(opts);
        if (opts.length === 1) {
          setSelectedLocation(opts[0]);
        }
        return opts;
      }
      setLocations([]); return [];
    } catch { setLocations([]); return []; }
  };

  const loadScheduled = async (fyId) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}scheduledNotifications`,
        {}, { financialYearId: fyId, questionType: "custom" }, "GET"
      );
      if (isSuccess && data) setScheduledDateData(data.data);
    } catch { }
  };

  const fetchHistory = async () => {
    if (!financialYearId) return;
    setHistoryLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/excel`,
        {}, { financialYearId }, "GET"
      );
      if (isSuccess && data) setHistoryData(Array.isArray(data) ? data : []);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ── Effects ── */

  useEffect(() => { if (financialYearId) loadScheduled(financialYearId); }, [financialYearId]);
  useEffect(() => { fetchPermission(); }, [companyId]);

  useEffect(() => {
    getLocations();
  }, [companyId]);

  useEffect(() => {
    if (!financialYearId || !selectedLocation) return;
    const startMonth = getStartingMonth() - 1;
    const fyValue = financialYear.find((p) => p.id === financialYearId)?.financial_year_value;
    if (!fyValue) return;
    const startYear = fyValue.substring(0, 4);
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(startYear, startMonth + i, 1);
      const name = date.toLocaleDateString("en-US", { month: "2-digit", year: "numeric" }).replace(",", "");
      const displayName = date.toLocaleDateString("en-US", { month: "short" });
      arr.push({ name, displayName });
    }
    setMonths(arr);
  }, [financialYearId, financialYear, selectedLocation?.id]);

  // Reset sublocation when location changes
  useEffect(() => {
    if (selectedLocation?.subLocations?.length === 1) {
      setSelectedSubLocation(selectedLocation.subLocations[0]);
    } else {
      setSelectedSubLocation(null);
    }
    setSelectedMonths([]);
  }, [selectedLocation?.id]);

  /* ── Handlers ── */

  const handleOpenUpload = async () => {
    setPermissionLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}company-template-permission`,
        {}, { companyId }, "GET", false, false
      );
      if (!isSuccess) {
        setUploadPermission({ templateDefined: true, isEnabled: false });
        sweetAlert("error", "Unable to verify upload permission right now.");
        return;
      }
      const pd = resolvePermissionPayload(data);
      const nextPerm = { templateDefined: true, isEnabled: Boolean(Number(resolveIsEnabled(pd))) };
      setUploadPermission(nextPerm);
      if (!nextPerm.isEnabled) {
        sweetAlert("error", "Upload is disabled for this company.");
        return;
      }
      await getLocations();
      setModalOpen(true);
    } catch {
      setUploadPermission({ templateDefined: true, isEnabled: false });
      sweetAlert("error", "Unable to verify upload permission right now.");
    } finally {
      setPermissionLoading(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  const generateTemplate = async () => {
    if (!selectedLocation) { sweetAlert("error", "Please select a location first."); return; }
    if (subLocationRequired && !selectedSubLocation) { sweetAlert("error", "Please select a sub-location first."); return; }
    if (!selectedMonths.length) { sweetAlert("error", "Please select at least one period first."); return; }

    const { data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}company-template-permission`,
      {}, { companyId }, "GET", false, false
    );
    const pd = resolvePermissionPayload(data);
    if (!Boolean(Number(resolveIsEnabled(pd)))) {
      sweetAlert("error", "Template download is disabled for this company.");
      return;
    }

    if (templateSource === "custom") {
      const filename = COMPANY_TEMPLATE_MAPPING[companyId];
      if (filename) {
        downloadTemplateFile(filename);
        return;
      }
      sweetAlert("error", "Custom template file is not registered for this company.");
      return;
    }

    try {
      const periods = buildPeriods(periodOptions);
      const fyValue = financialYear.find((f) => f.id === financialYearId)?.financial_year_value;
      const cu = JSON.parse(localStorage.getItem("currentUser") || "null");
      await generateStandardTemplate({
        financialYearId,
        sourceId: selectedLocation.id,
        periods,
        companyLabel: cu?.register_company_name,
        fyValue,
      });
    } catch (err) {
      sweetAlert("error", err?.message || "Unable to generate template.");
    }
  };

  const handleFileChange = (file) => {
    if (!file) return;
    const allowed = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!allowed.includes(file.type)) { sweetAlert("error", "Please select a valid Excel file (.xlsx or .xls)"); return; }
    if (file.size > 10 * 1024 * 1024) { sweetAlert("error", "File size must be less than 10MB"); return; }
    setSelectedFile(file);
  };

  /**
   * Reports the outcome of an upload.
   *
   * Rows whose KPI is past its due date are skipped rather than failed, so they
   * are counted separately and named with their deadline — a silent "rows saved"
   * would otherwise leave the uploader believing data landed that never did.
   * Rows carrying a unit the KPI does not accept are skipped the same way, and
   * since that is the uploader's to fix, the report names the units the KPIs are
   * fixed to rather than giving a bare count.
   * @param {object} responseData the upload response.
   */
  const handleSuccess = useCallback((responseData) => {
    setSelectedLocation(null);
    setSelectedSubLocation(null);
    setSelectedMonths([]);
    setSelectedFile(null);
    setModalOpen(false);
    const {
      rowsProcessed,
      successfulUploads,
      failedUploads,
      skippedUploads,
      skipped,
      auditedSkippedUploads,
      unitMismatchUploads,
      unitMismatchSkipped,
    } = responseData?.data ?? {};

    if (rowsProcessed == null) {
      sweetAlert("success", "Data successfully uploaded.");
      return;
    }

    const parts = [`${successfulUploads ?? 0} rows saved`];
    if (failedUploads > 0) parts.push(`${failedUploads} failed`);
    if (skippedUploads > 0) parts.push(`${skippedUploads} skipped — past their due date`);
    if (auditedSkippedUploads > 0) parts.push(`${auditedSkippedUploads} skipped — already audited`);
    if (unitMismatchUploads > 0) parts.push(`${unitMismatchUploads} skipped — wrong unit`);

    const deadlines = [...new Set((skipped ?? []).map((row) => row.dueDate).filter(Boolean))];
    let detail = deadlines.length
      ? ` Closed for entry on ${deadlines.join(", ")}. Request access from an administrator to reopen.`
      : "";

    const expectedUnits = [
      ...new Set((unitMismatchSkipped ?? []).map((row) => row.expectedUnit).filter(Boolean)),
    ];
    if (expectedUnits.length) {
      detail += ` Those KPIs accept data in ${expectedUnits.join(", ")} only — download a fresh template and re-upload.`;
    }

    sweetAlert(
      failedUploads > 0 || skippedUploads > 0 || auditedSkippedUploads > 0 || unitMismatchUploads > 0
        ? "warning"
        : "success",
      `Upload complete — ${parts.join(", ")}.${detail}`,
    );
  }, []);

  /**
   * Build periods array from current selection.
   * Monthly: each entry is one month → period covering that month.
   * Quarterly: each entry contains months[] → period spanning the full quarter.
   * Always includes a human-readable `label` for the BE-generated template headers.
   */
  const buildPeriods = (selected) => {
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    return selected.map((entry) => {
      if (Array.isArray(entry.months) && entry.months.length) {
        const first = entry.months[0];
        const last = entry.months[entry.months.length - 1];
        const [fm, fy] = first.name.split("/");
        const [lm, ly] = last.name.split("/");
        const from = new Date(Number(fy), Number(fm) - 1, 1);
        const to = new Date(Number(ly), Number(lm), 0);
        return { fromDate: fmt(from), toDate: fmt(to), label: entry.label };
      }
      const [mm, yyyy] = entry.name.split("/");
      const from = new Date(Number(yyyy), Number(mm) - 1, 1);
      const to = new Date(Number(yyyy), Number(mm), 0);
      return { fromDate: fmt(from), toDate: fmt(to), label: `${entry.displayName} ${yyyy}` };
    });
  };

  const handleSave = async () => {
    if (!selectedLocation) { sweetAlert("error", "Please select a location"); return; }
    if (subLocationRequired && !selectedSubLocation) { sweetAlert("error", "Please select a sub-location"); return; }
    if (!selectedMonths.length) { sweetAlert("error", "Please select at least one month"); return; }
    if (!selectedFile) { sweetAlert("error", "Please upload an Excel file"); return; }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("financialYearId", String(financialYearId));
      formData.append("sourceId", String(selectedLocation.id));
      formData.append(
        "subLocationId",
        selectedSubLocation ? String(selectedSubLocation.id) : "0"
      );
      // periods must be sent as JSON string since FormData is multipart
      formData.append("periods", JSON.stringify(buildPeriods(selectedMonths)));

      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantitative-data/excel`,
        {},
        formData,
        "POST",
        false,
        false,
        true   // isFormData flag — tells apiCall to skip JSON.stringify / Content-Type header
      );

      if (!isSuccess) {
        sweetAlert("error", data?.message || data?.error || "Upload failed. Please try again.");
        return;
      }
      handleSuccess(data);
      window.location.reload();
    } catch (err) {
      console.error("Excel upload error:", err);
      sweetAlert("error", "An unexpected error occurred during upload.");
    } finally {
      setLoading(false);
    }
  };

  // subLocation required if selected location has subLocations
  const subLocationRequired = selectedLocation?.subLocations?.length > 0;
  const subLocationSatisfied = !subLocationRequired || selectedSubLocation !== null;

  const canSave = uploadPermission.templateDefined && uploadPermission.isEnabled &&
    selectedLocation && subLocationSatisfied && selectedMonths.length > 0 && selectedFile && !loading && !permissionLoading;

  const templateReady = Boolean(
    selectedLocation && subLocationSatisfied && selectedMonths.length > 0,
  );

  /* ── Styles ── */

  const S = {
    toolbarBtn: (variant = "primary") => ({
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "0 16px", height: 36, borderRadius: 8, fontWeight: 600,
      fontSize: 13, border: "none", cursor: "pointer", transition: "all .18s",
      ...(variant === "primary"
        ? { background: "#3f88a5", color: "#fff" }
        : { background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }),
    }),
    label: { fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6, display: "block" },
    sectionTitle: { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#94a3b8", textTransform: "uppercase", marginBottom: 12 },
    infoBox: {
      background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10,
      padding: "10px 14px", marginBottom: 16, display: "flex",
      justifyContent: "space-between", alignItems: "center",
    },
  };

  /* ── Render ── */

  return (
    <>
      {/* ── Toolbar ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button style={S.toolbarBtn("primary")} onClick={handleOpenUpload} disabled={permissionLoading}>
          {permissionLoading
            ? <><span className="spinner-border spinner-border-sm" /> Checking…</>
            : <><UploadIcon /> Upload</>}
        </button>
        <button style={S.toolbarBtn("ghost")} onClick={handleOpenHistory}>
          <HistoryIcon /> History
        </button>
      </div>

      {/* ══ Upload Modal ══════════════════════════════════════════ */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9", padding: "18px 24px" }}>
          <Modal.Title style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
            Upload Excel Data
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>
          {loading || permissionLoading ? (
            <LoadingState label={permissionLoading ? "Checking permission…" : "Uploading…"} />
          ) : !uploadPermission.templateDefined ? (
            <Alert variant="warning">No template defined for this company.</Alert>
          ) : !uploadPermission.isEnabled ? (
            <Alert variant="danger">Upload is disabled for this company.</Alert>
          ) : (
            <>
              <p style={{
                fontSize: 12, color: "#b45309", background: "#fffbeb",
                border: "1px solid #fde68a", borderRadius: 6,
                padding: "8px 12px", marginBottom: 16,
              }}>
                <span aria-hidden style={{ marginRight: 6 }}>⚠</span>
                Location and period selection are mandatory.
              </p>

              {/* Step 1 – Location + optional Sub-location */}
              <div style={{ marginBottom: 20 }}>
                <span style={S.sectionTitle}>Step 1 — Location</span>
                <div style={{ display: "grid", gridTemplateColumns: selectedLocation?.subLocations?.length > 0 ? "1fr 1fr" : "1fr", gap: 10 }}>
                  <SearchableSingleSelect
                    options={locations.map(l => ({ value: l.id, label: l.name, raw: l }))}
                    value={selectedLocation ? { value: selectedLocation.id, label: selectedLocation.name } : null}
                    onChange={(opt) => setSelectedLocation(opt ? opt.raw : null)}
                    placeholder="Choose a location"
                  />
                  {selectedLocation?.subLocations?.length > 0 && (
                    <SearchableSingleSelect
                      options={selectedLocation.subLocations.map(s => ({ value: s.id, label: s.name }))}
                      value={selectedSubLocation ? { value: selectedSubLocation.id, label: selectedSubLocation.name } : null}
                      onChange={(opt) => setSelectedSubLocation(opt ? { id: opt.value, name: opt.label } : null)}
                      placeholder="Choose a sub-location"
                      required
                    />
                  )}
                </div>
                {selectedLocation?.subLocations?.length > 0 && !selectedSubLocation && (
                  <p style={{ fontSize: 11, color: "#f59e0b", marginTop: 5, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 13 }}>⚠</span> Sub-location is required for this location
                  </p>
                )}
              </div>

              {/* Step 2 – Periods */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={S.sectionTitle}>Step 2 — Period</span>
                  <div role="radiogroup" aria-label="Period granularity" style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 999, padding: 3 }}>
                    {[
                      { key: "monthly", label: "Monthly" },
                      { key: "quarterly", label: "Quarterly" },
                    ].map((opt) => {
                      const active = periodGranularity === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setPeriodGranularity(opt.key)}
                          style={{
                            border: "none",
                            background: active ? "#fff" : "transparent",
                            boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                            color: active ? "#0f172a" : "#64748b",
                            borderRadius: 999, padding: "4px 14px",
                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                            transition: "all .15s",
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <MonthMultiSelect
                  options={periodOptions}
                  selected={selectedMonths}
                  onChange={setSelectedMonths}
                  disabled={!selectedLocation || (subLocationRequired && !selectedSubLocation)}
                  placeholder={periodGranularity === "monthly" ? "Select month(s)" : "Select quarter(s)"}
                  selectAllLabel={periodGranularity === "monthly" ? "All months" : "All quarters"}
                />
                {!selectedLocation && (
                  <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>Select a location first to choose {periodGranularity === "monthly" ? "months" : "quarters"}.</p>
                )}
                {selectedLocation && subLocationRequired && !selectedSubLocation && (
                  <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>Select a sub-location first to choose {periodGranularity === "monthly" ? "months" : "quarters"}.</p>
                )}
              </div>

              {/* Download template row */}
              <div style={{ ...S.infoBox, flexDirection: "column", alignItems: "stretch", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "#0369a1" }}>
                    <InfoIcon /> Download your template once location & period are set.
                  </span>
                  <button
                    onClick={generateTemplate}
                    disabled={!templateReady || templateLoading}
                    style={{
                      background: templateReady && !templateLoading ? "#0284c7" : "transparent",
                      border: "1px solid #0284c7",
                      color: templateReady && !templateLoading ? "#fff" : "#0284c7",
                      borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600,
                      cursor: templateReady && !templateLoading ? "pointer" : "not-allowed",
                      opacity: templateReady && !templateLoading ? 1 : 0.6,
                      display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    {templateLoading
                      ? <><span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} /> Generating…</>
                      : <><DownloadIcon /> Template</>}
                  </button>
                </div>

              </div>

              {/* Step 3 – File */}
              <div>
                <span style={S.sectionTitle}>Step 3 — File</span>
                <DropZone
                  file={selectedFile}
                  dragOver={dragOver}
                  onDragOver={() => setDragOver(true)}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileChange(e.dataTransfer.files[0]); }}
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  onClear={() => setSelectedFile(null)}
                />
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", padding: "14px 24px", gap: 8 }}>
          <button
            onClick={() => { setModalOpen(false); setSelectedFile(null); setSelectedLocation(null); setSelectedSubLocation(null); setSelectedMonths([]); }}
            style={{
              background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b",
              borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              background: canSave ? "#3f88a5" : "#94a3b8", color: "#fff",
              border: "none", borderRadius: 8, padding: "8px 22px",
              fontWeight: 600, fontSize: 13, cursor: canSave ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", gap: 6, transition: "background .2s",
            }}
          >
            <UploadIcon size={14} /> Save & Upload
          </button>
        </Modal.Footer>
      </Modal>

      {/* ══ History Modal ═════════════════════════════════════════ */}
      <Modal show={historyOpen} onHide={() => setHistoryOpen(false)} size="xl" centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9", padding: "18px 24px" }}>
          <Modal.Title style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
            Upload History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, minHeight: 320 }}>
          {historyLoading ? (
            <LoadingState label="Loading history…" />
          ) : historyData.length === 0 ? (
            <EmptyHistory />
          ) : (
            <>
              {/* Table header */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "28px 1fr 1fr 90px 80px",
                gap: 12, padding: "10px 16px",
                background: "#f8fafc", borderBottom: "2px solid #e2e8f0",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                color: "#94a3b8", textTransform: "uppercase",
              }}>
                <span>#</span>
                <span>Date</span>
                <span>Period(s)</span>
                <span>Rows</span>
                <span>Status</span>
              </div>
              <div style={{ maxHeight: 440, overflowY: "auto" }}>
                {historyData.map((r, i) => <HistoryRow key={r.id} record={r} idx={i} />)}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", padding: "12px 24px" }}>
          <button
            onClick={fetchHistory}
            style={{
              background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#475569",
              borderRadius: 8, padding: "7px 16px", fontWeight: 600, fontSize: 13,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <RefreshIcon /> Refresh
          </button>
          <button
            onClick={() => setHistoryOpen(false)}
            style={{
              background: "#3f88a5", color: "#fff", border: "none",
              borderRadius: 8, padding: "7px 18px", fontWeight: 600,
              fontSize: 13, cursor: "pointer", marginLeft: 8,
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

/* ─── Small sub-components ─────────────────────────────────────── */

const LoadingState = ({ label }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 0", gap: 12 }}>
    <div className="spinner-border text-primary" role="status" />
    <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>{label}</p>
  </div>
);

const EmptyHistory = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "56px 0", gap: 10 }}>
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ width: 40, height: 40, minWidth: 40, minHeight: 40, display: "block" }}>
      <path d="M9 17H7A5 5 0 0 1 7 7h2" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
    </svg>
    <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>No upload history found for this financial year.</p>
  </div>
);

const DropZone = ({ file, dragOver, onDragOver, onDragLeave, onDrop, onChange, onClear }) => (
  <div
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    style={{
      position: "relative", borderRadius: 10,
      border: `2px dashed ${dragOver ? "#3f88a5" : file ? "#22c55e" : "#cbd5e1"}`,
      background: dragOver ? "#f0f9ff" : file ? "#f0fdf4" : "#fafbfc",
      padding: "28px 20px", textAlign: "center", transition: "all .2s",
    }}
  >
    {file ? (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <ExcelIcon />
        <span style={{ fontWeight: 600, color: "#166534", fontSize: 14 }}>{file.name}</span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{(file.size / 1024).toFixed(1)} KB</span>
        <button
          onClick={onClear}
          style={{
            marginTop: 4, background: "transparent", border: "1px solid #fca5a5",
            color: "#ef4444", borderRadius: 6, padding: "3px 10px", fontSize: 12,
            fontWeight: 600, cursor: "pointer",
          }}
        >
          Remove
        </button>
      </div>
    ) : (
      <>
        <ExcelIcon />
        <p style={{ color: "#64748b", fontSize: 14, margin: "8px 0 2px" }}>
          <strong>Drag & drop</strong> or <span style={{ color: "#3f88a5" }}>browse</span>
        </p>
        <p style={{ color: "#94a3b8", fontSize: 12, margin: 0 }}>Supports .xlsx and .xls · Max 10 MB</p>
      </>
    )}
    <input
      type="file"
      accept=".xlsx,.xls"
      onChange={onChange}
      style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
    />
  </div>
);

/* ─── Icons (inline SVG, no deps) ─────────────────────────────── */

const iconStyle = (size = 14) => ({
  width: size,
  height: size,
  minWidth: size,
  minHeight: size,
  display: "inline-block",
  verticalAlign: "middle",
  flexShrink: 0,
});

const UploadIcon = ({ size = 14 }) => (
  <svg style={iconStyle(size)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const HistoryIcon = ({ size = 14 }) => (
  <svg style={iconStyle(size)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="12 8 12 12 14 14" />
    <path d="M3.05 11a9 9 0 1 0 .5-4" />
    <polyline points="3 3 3 9 9 9" />
  </svg>
);

const DownloadIcon = ({ size = 13 }) => (
  <svg style={iconStyle(size)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const InfoIcon = ({ size = 13 }) => (
  <svg style={{ ...iconStyle(size), marginRight: 4 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const RefreshIcon = ({ size = 13 }) => (
  <svg style={iconStyle(size)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const ExcelIcon = ({ size = 53 }) => (
  <svg
    viewBox="0 0 48 48" fill="none"
    style={{ display: "block", margin: "0 auto 4px", width: size, height: size, minWidth: size, minHeight: size, flexShrink: 0 }}
  >
    <path d="M14 10h14l10 10v22a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2z" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" />
    <path d="M28 10v10h10" stroke="#22c55e" strokeWidth="1.5" strokeLinejoin="round" fill="#bbf7d0" />
    <line x1="13" y1="29" x2="19" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="19" y1="29" x2="13" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="22" y1="29" x2="22" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="22" y1="38" x2="27" y2="38" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M36 30.5c0-1-1-1.5-2.5-1.5s-2.5.6-2.5 1.5c0 2 5 1.5 5 3.5 0 1.1-1.1 2-2.5 2s-2.5-.7-2.5-2" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

export default ExcelUploader;